import { useEffect, useState } from 'react';
import { Button } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// services
import NotificationService from 'core/services/notification';
import { onGetPermissions, onGetRole, onUpdateRole } from 'modules/roles/services/roles';

// models
import { ModifiedPermission } from 'modules/roles/models/modifiedPermission';
import { Role } from 'modules/roles/models/roles';

// components
import ModulesPermissionsComponent from 'modules/roles/components/modulesPermission/modulesPermission';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import BackButton from 'components/backButton/backButton';

function UpdateRolesPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [role, setRole] = useState<Role>();
	const [permissions, setPermissions] = useState<ModifiedPermission[]>([]);
	const [selectedPermissions, setSelectedPermission] = useState<number[]>([]);

	useEffect(() => {
		getData();
	}, []);

	const getData = async () => {
		try {
			// get role data and its permissions
			const roleResponse = await onGetRole(id as string);
			const roleData = roleResponse.data.data;
			setSelectedPermission(roleData.permissions.map((el) => el.id));
			setRole(roleData);

			// get all permissions to view
			const permissionsResponse = await onGetPermissions();
			const permissions = permissionsResponse.data.data.permissions;
			handlePermissionsView(permissions, roleData);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			navigate('/roles');
		}
	};

	const handlePermissionsView = (permissionResponse: any, roleData: Role) => {
		const result: any = [];

		// Function to find or create the permission set for each entity (e.g., profile, role)
		const findOrCreatePermission = (name: string) => {
			let existing = result.find((item: any) => item.name === name);
			if (!existing) {
				existing = {
					name,
					create: { id: null, value: null },
					update: { id: null, value: null },
					delete: { id: null, value: null },
					view: { id: null, value: null },
				};
				result.push(existing);
			}
			return existing;
		};

		// Iterate over the permissions array
		permissionResponse.forEach((permission: any) => {
			// Split the permission name into parts (e.g., "profile_update" -> ["profile", "update"])
			const [entity, action, dummy, dummy2, sliderAction] = permission.name.split('_');

			// Find or create the permission set for the entity
			const permissionSet = findOrCreatePermission(entity);

			//check for wallet as it have only UPDATE action
			if (entity === 'wallet') {
				permissionSet.update = { id: permission.id, value: 0 };
				return;
			}

			// check for mobile slider
			if (entity === 'mobile') {
				if (sliderAction === 'create') permissionSet.create = { id: permission.id, value: 0 };
				if (sliderAction === 'update') permissionSet.update = { id: permission.id, value: 0 };
				if (sliderAction === 'delete') permissionSet.delete = { id: permission.id, value: 0 };
				if (sliderAction === 'view') permissionSet.view = { id: permission.id, value: 0 };
				return;
			}

			// Set the appropriate action flag based on the action (create, update, delete, view)
			if (action === 'create') permissionSet.create = { id: permission.id, value: 0 };
			if (action === 'update') permissionSet.update = { id: permission.id, value: 0 };
			if (action === 'delete') permissionSet.delete = { id: permission.id, value: 0 };
			if (action === 'view') permissionSet.view = { id: permission.id, value: 0 };
		});

		const mapPermissionAction = (action: string, permissionList: any) => {
			return permissionList.some((p: any) => p.name === action) ? 1 : 0;
		};

		// Update permissions with available permissions
		result.forEach((permission: any, index: number) => {
			// check for wallet management
			if (permission.name === 'wallet') {
				permission.update.value = roleData.permissions.findIndex((el) => el.name === 'wallet_management') >= 0 ? 1 : 0;
				permission.key = index; // for Table in ant design package
				return;
			}

			// check for mobile splash screens
			if (permission.name === 'mobile') {
				permission.create.value = mapPermissionAction(`mobile_app_intro_slider_create`, roleData.permissions);
				permission.update.value = mapPermissionAction(`mobile_app_intro_slider_update`, roleData.permissions);
				permission.delete.value = mapPermissionAction(`mobile_app_intro_slider_delete`, roleData.permissions);
				permission.view.value = mapPermissionAction(`mobile_app_intro_slider_view`, roleData.permissions);
				permission.key = index; // for Table in ant design package
				return;
			}

			permission.create.value = mapPermissionAction(`${permission.name}_create`, roleData.permissions);
			permission.update.value = mapPermissionAction(`${permission.name}_update`, roleData.permissions);
			permission.delete.value = mapPermissionAction(`${permission.name}_delete`, roleData.permissions);
			permission.view.value = mapPermissionAction(`${permission.name}_view`, roleData.permissions);
			permission.key = index; // for Table in ant design package
		});

		setPermissions(result);
	};

	const onUpdatePermission = (id: number) => {
		const idIndex = selectedPermissions.findIndex((el) => el === id);

		if (idIndex >= 0) {
			const updatedPermissions = selectedPermissions.filter((_, index) => index !== idIndex);
			setSelectedPermission(updatedPermissions);
		} else {
			const updatedPermissions = [...selectedPermissions, id];
			setSelectedPermission(updatedPermissions);
		}
	};

	const onSubmit = async () => {
		try {
			setIsSubmitting(true);
			await onUpdateRole(id as string, selectedPermissions);
			NotificationService.success(t('COMMON.SUCCESS'), t('ROLES.ROLE_UPDATED'));
			navigate('/roles');
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('ROLES.ROLE_UPDATE_FAILED'));
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('ROLES.UPDATE_ROLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<div className="sectionContainer">
						<h5 style={{ marginBottom: 15 }}>{t('ROLES.ROLE_NAME')}</h5>
						<h5>{role?.name}</h5>
					</div>

					<div className="sectionContainer">
						<h5 style={{ marginBottom: 15 }}>{t('ROLES.ADMIN_ROLE')}</h5>
						<ModulesPermissionsComponent permissions={permissions} onPermissionChange={onUpdatePermission} />

						<Button
							size="large"
							type="primary"
							htmlType="submit"
							form="createForm"
							loading={isSubmitting}
							style={{ marginTop: 20, marginInlineStart: 'auto', display: 'flex' }}
							onClick={onSubmit}>
							{t('COMMON.UPDATE')}
						</Button>
					</div>
				</>
			)}
		</>
	);
}

export default UpdateRolesPage;
